// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AutoCompleteComponent, BaseInputSearch, BaseSelect, Loading, UnderDevelopment } from '~/components/common';
import { Controller, FormProvider, useFormContext, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { TableQuoteItem } from '~/components/module/quotes';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeInventoryGroupDay } from '~/utils/enum/common';
import { IFormQuotes, IQuoteListItem } from '~/utils/interface/quotes';
import { IGroupDay, IParamInventoryQuote } from '~/utils/interface/inventory';
import { IBaseOption, IPayloadCallApiFn, IQueryParamBase, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { ICategory } from '~/utils/interface/category';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { getDatesInRange, handleDaysBetween } from '~/utils/helper/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { getListInventoryQuote } from '~/thunks/inventory/inventoryThunk';
// Styles, images, icons
import styles from './QuoteItemsForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const QuoteItemsForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const { id: quoteId } = useParams();
  const dispatch = useAppDispatch();

  const {
    control,
    watch,
    trigger,
    setValue,
    resetField,
    getValues,
    formState: { errors, touchedFields, isSubmitted, isDirty, dirtyFields },
  } = useFormContext<IFormQuotes>();

  const {
    startDate,
    endDate,
    quoteGroups,
    selectGroupQuote,
    quoteItems,
    quoteAddOns,
    quoteItemQuantity,
    templateQuoteId,
  } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterCategory, setFilterCategory] = useState<string[]>([]);
  const debounceFilterCategory = useDebounce(filterCategory, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [searchKey, setSearchKey] = useState<string | null>(null);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IParamInventoryQuote>({
    page: DEFAULT_CURRENT_PAGE,
    limit: Number(quoteItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(quoteItemQuantity) : DEFAULT_LIMIT_PAGE,
    quoteId: quoteId || undefined,
  });

  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isCheckDetail, setIsCheckDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook

  useEffect(() => {
    if (!startDate || !endDate) return;

    const newParams: IParamInventoryQuote = {
      ...paramObject,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: Number(quoteItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(quoteItemQuantity) : DEFAULT_LIMIT_PAGE,
    };

    setParamObject(newParams);
  }, [startDate, endDate, quoteItemQuantity]);

  useEffect(() => {
    if (!paramObject || !paramObject.startDate || !paramObject.endDate) return;

    handleGetListInventory(paramObject);
  }, [paramObject, templateQuoteId]);

  useEffect(() => {
    if (!quoteItems || isCheckDetail || !quoteId) return;

    const newParams: IParamInventoryQuote = {
      ...paramObject,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: Number(quoteItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(quoteItemQuantity) : DEFAULT_LIMIT_PAGE,
    };

    quoteItems?.length > 0 && setIsCheckDetail(true);

    setParamObject(newParams);
  }, [JSON.stringify(quoteItems)]);

  useEffect(() => {
    if (searchKey === null) return;

    setParamObject({
      ...paramObject,
      ...(startDate && endDate && { startDate, endDate }),
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (debounceFilterCategory === null) return;

    setParamObject({
      ...paramObject,
      ...(startDate && endDate && { startDate, endDate }),
      ...(debounceFilterCategory ? { categoryId: debounceFilterCategory } : { categoryId: undefined }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceFilterCategory]);

  useEffect(() => {
    if (!quoteGroups) return;

    const dataGroupOption: IBaseOption[] =
      quoteGroups?.map((item) => ({ value: item.id || EMPTY_STRING, label: item.name })) || [];

    const isFindGroup = dataGroupOption.find((item) => item.value === selectGroupQuote);

    if (isFindGroup) return;

    setValue('selectGroupQuote', EMPTY_STRING);
  }, [JSON.stringify(quoteGroups)]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInventory = (params: IParamInventoryQuote) => {
    setIsLoading(true);

    dispatch(getListInventoryQuote(params))
      .unwrap()
      .then((res) => {
        const { responses: listInventory, pagination } = res?.data;

        if (!listInventory) return;

        const dataInventory: IQuoteListItem[] = listInventory.map((item) => {
          let totalQuantity = 0;

          quoteItems?.map((quoteItem) => {
            if (
              quoteItem?.inventoryId === item.id &&
              String(quoteItem?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
            ) {
              totalQuantity += quoteItem.quantity || 0;
            }
          });

          const missingDay =
            startDate &&
            endDate &&
            item.groupDayOrder?.length
              ? hasMissingDay(startDate, endDate, item.groupDayOrder)
              : true;

          if (!item.groupDayOrder?.length) {
            return {
              ...item,
              quantity: String(totalQuantity),
              quantityInventory: item.quantity,
            };
          }

          const quantityInventory = missingDay
            ? item.quantity
            : String(
              Math.min(
                ...item.groupDayOrder.map(o =>
                  Number(o.availableInventory)
                )
              )
            );

          return {
            ...item,
            quantity: String(totalQuantity),
            quantityInventory
          };
        });

        setValue('listQuoteItems', convertDataInventoryGroupDate(dataInventory));

        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const hasMissingDay = (
    startDate: string,
    endDate: string,
    groupDayOrder: any[]
  ) => {
    const rangeDates = getDatesInRange(startDate, endDate);
    const orderDays = groupDayOrder.map(d => d.day);

    return rangeDates.some(date => !orderDays.includes(date));
  };

  const convertDataInventoryGroupDate = (quoteItems: IQuoteListItem[]) => {
    const sortDated = handleDaysBetween(
      startDate || EMPTY_STRING,
      endDate || EMPTY_STRING
    );

    return quoteItems.map((item) => {
      const convertQuotes = item.groupDayQuote?.map((quote) => ({
        ...quote,
        type: TypeInventoryGroupDay.QUOTE,
        availableInventory: quote.availableInventory ?? String(quote.quantity),
      }));

      const convertOrders = item.groupDayOrder?.map((order) => ({
        ...order,
        type: TypeInventoryGroupDay.ORDER,
        availableInventory: order.availableInventory ?? String(order.quantity),
      }));

      const mergedDays: IGroupDay[] = [];

      sortDated.forEach((date) => {
        const quote = convertQuotes?.find((q) => q.day === date);
        const order = convertOrders?.find((o) => o.day === date);

        if (quote && order) {
          mergedDays.push({
            ...order,
            type: TypeInventoryGroupDay.ALL,
            availableInventory: String(order.availableInventory),
          });
        } else if (order) {
          mergedDays.push(order);
        } else if (quote) {
          mergedDays.push(quote);
        } else {
          mergedDays.push({
            day: date,
            quantity: 0,
            availableInventory: item.quantityInventory ,
            type: TypeInventoryGroupDay.DEFAULT
          });
        }
      });

      return { ...item, mergedDays };
    });
  };

  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const fetchOptionsGroup = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const dataGroupOption: IBaseOption[] =
      quoteGroups?.map((item) => ({ value: item.id || EMPTY_STRING, label: item.name })) || [];

    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: dataGroupOption,
      hasMoreResp: false,
    };

    return result;
  }, [JSON.stringify(quoteGroups)]);

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <FormProvider
      {...({
        control,
        resetField,
        watch,
        trigger,
        setValue,
        getValues,
        formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted },
      } as UseFormReturn<IFormQuotes>)}
    >
      <Stack className={cx('container')}>
        <Stack className={cx('freeze')}>
          <Stack direction={'row'} spacing={'10px'} alignItems={'center'}>
            <img src={icons.commonIconQuoteItems} alt={t('common_alt_img', { ns: 'common/base' })} />
            <Typography className={cx('title')}>{t('form_quotes_title_quote_items')}</Typography>
          </Stack>

          <Box className={cx('viewAction')}>
            <Box className={cx('viewSearch')}>
              <BaseInputSearch
                typeStyle='outlined'
                onChange={handleSearchChange}
                borderRadius={100}
                placeholder={t('form_quotes_items_title_search_products')}
              />
            </Box>

            <Stack width={546} gap={4} direction={'row'} alignItems={'center'}>
              <AutoCompleteComponent
                multiple={true}
                callApiFn={fetchOptionsCategory}
                placeholder={t('form_quotes_items_title_search_category')}
                value={filterCategory}
                width={330}
                onChange={(selected) => {
                  if (Array.isArray(selected)) {
                    setFilterCategory(selected.map((item) => item?.value));
                  }
                }}
              />

              <Controller
                name={`selectGroupQuote`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <BaseSelect
                    gap={10}
                    height={36}
                    value={value}
                    styleType='extra'
                    placeholder={t('form_quotes_items_title_select_group')}
                    callApiFn={fetchOptionsGroup}
                    onChange={(item) => {
                      onChange(item?.value);
                      setValue('selectIdProductQuote', EMPTY_STRING);
                      setValue('selectIdVariantQuote', EMPTY_STRING);
                      setValue('selectGroupItem', EMPTY_STRING);
                      setValue('selectIdProductGroup', EMPTY_STRING);
                      setValue('selectIdVariantGroup', EMPTY_STRING);
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </Stack>

        <TableQuoteItem
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          onPageChange={handlePageChange}
        />

        {isLoading && <Loading />}

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
      </Stack>
    </FormProvider>
  );
};

export default QuoteItemsForm;
